import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { CalendarBlank, WarningCircle, CheckCircle, ArrowSquareOut } from "@phosphor-icons/react";

/**
 * Outlook / Microsoft Graph status panel in Settings.
 * Reads /outlook/status — shows configured mailbox or a list of missing env vars
 * plus a compact setup checklist.
 */
export default function OutlookStatus() {
  const [status, setStatus] = useState(null);
  const [subscribing, setSubscribing] = useState(false);

  const load = () => api.get("/outlook/status").then((r) => setStatus(r.data)).catch(() => setStatus({ configured: false, missing: ["all"] }));
  useEffect(() => { load(); }, []);

  const subscribe = async () => {
    setSubscribing(true);
    try {
      const { data } = await api.post("/outlook/subscribe");
      toast.success(`Subscribed — expires ${data.expires_at?.slice(0, 10)}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Subscribe failed");
    } finally { setSubscribing(false); }
  };

  if (!status) return null;

  return (
    <div className="border border-border bg-card p-6 md:col-span-2" data-testid="outlook-status-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="text-[0.625rem] font-mono uppercase tracking-widest text-eyebrow flex items-center gap-1.5">
          <CalendarBlank size={12} weight="fill" /> Outlook calendar sync
        </div>
        {status.configured ? (
          <span className="text-[0.625rem] font-mono uppercase tracking-widest text-emerald-500 flex items-center gap-1">
            <CheckCircle size={12} weight="fill" /> connected · {status.mailbox}
          </span>
        ) : (
          <span className="text-[0.625rem] font-mono uppercase tracking-widest text-amber-500 flex items-center gap-1">
            <WarningCircle size={12} weight="fill" /> not configured
          </span>
        )}
      </div>

      {status.configured ? (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            The shared firm mailbox <span className="font-mono text-foreground">{status.mailbox}</span> is authenticated via Microsoft Graph. Engagement sessions can now be created directly on the firm calendar and meeting-acceptance changes will flow back automatically.
          </div>
          <Button
            onClick={subscribe}
            disabled={subscribing}
            variant="outline"
            className="rounded-none border-primary/60 text-primary hover:bg-primary/10"
            data-testid="outlook-subscribe-btn"
          >
            {subscribing ? "Registering…" : "Register webhook subscription"}
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="text-sm text-muted-foreground">
            To activate two-way calendar sync, ask your Microsoft 365 admin to register an app-only Graph client and set these env vars:
          </div>
          <div className="border-l-2 border-primary/40 pl-4 space-y-1">
            {(status.missing || []).map((k) => (
              <div key={k} className="text-xs font-mono text-muted-foreground">
                <span className="text-destructive">MISSING</span> {k}
              </div>
            ))}
          </div>
          <ol className="text-xs text-muted-foreground leading-relaxed list-decimal ml-4 space-y-1">
            <li>Register a new app in Entra ID → Application permissions: <span className="font-mono text-foreground">Calendars.ReadWrite</span>, <span className="font-mono text-foreground">Calendars.Read</span>. Grant admin consent.</li>
            <li>Create a client secret. Store the four values in <span className="font-mono text-foreground">/app/backend/.env</span> as <span className="font-mono">AZURE_TENANT_ID</span>, <span className="font-mono">AZURE_CLIENT_ID</span>, <span className="font-mono">AZURE_CLIENT_SECRET</span>, <span className="font-mono">GRAPH_SHARED_MAILBOX</span>.</li>
            <li>Restart the backend, come back to this panel — the mailbox appears above and you can register the webhook subscription.</li>
          </ol>
          <a href="https://learn.microsoft.com/en-us/graph/auth-v2-service" target="_blank" rel="noreferrer" className="text-xs text-primary hover:underline inline-flex items-center gap-1">
            <ArrowSquareOut size={11} /> Microsoft Graph app-only auth docs
          </a>
        </div>
      )}
    </div>
  );
}
